import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import {
	Form,
	Input,
	Button,
	Row,
	Col,
	DatePicker,
	Alert,
	InputNumber,
	Radio,
	message,
	Popconfirm,
	Modal,
} from 'antd';
import moment from 'moment';
import { InvoiceFormConfig } from './types';
import { getCurrency, nullSafeGet } from '../../utils/DataAccessUtils';
import { getInvoiceDueDateBasedOnPaymentTerms } from '../../utils/InvoiceUtils';
import OWRichTextEditor from '../../rich_text_editor/OWRichTextEditor';
import { DotSeparator } from '../common/FormDots';
import CostSection from '../SupplierInvoiceFormNew/CostSection';
import TaxFormFieldsNew from './TaxFormFieldsNew';
import InvoicePaymentTermComponent from '../buyer_invoice_form/InvoicePaymentTermComponent';
import FileUploader from '../file_uploader/FileUploader';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	initialiseFormDataWithLineItems,
	initializeLineItemKeys,
	initializeInvoiceTotalFields,
	getFixedFloatStringOrZero,
} from '../../utils/InvoiceUtils';
import { TIME_IN } from '../supplier_invoice_form/LaborTimeSelect';
import { useParsedFieldIndicator } from './useParsedFieldIndicator';
import { ExclamationCircleFilled, ExclamationCircleOutlined } from '@ant-design/icons';

const DATE_FORMAT = 'MM-DD-YYYY';

interface InvoiceFormBaseProps {
	config: InvoiceFormConfig;
	handleCancel?: () => void;
}

const InvoiceFormBase: React.FC<InvoiceFormBaseProps> = ({ config, handleCancel }) => {
	const [form] = Form.useForm();
	const editorRef = useRef(null);
	const [formDataInitialized, setFormDataInitialized] = useState(false);
	const [currency, setCurrency] = useState<any>(null);
	const [totalBeforeTax, setTotalBeforeTax] = useState(0);
	const [totalAfterTax, setTotalAfterTax] = useState(0);
	const [timeIn] = useState(TIME_IN.HOURS);
	const [taxKeys, setTaxKeys] = useState<any[]>([]);
	const [showSignatureModal, setShowSignatureModal] = useState(false);
	const [signatureName, setSignatureName] = useState('');
	const [pendingSubmit, setPendingSubmit] = useState(false);
	const [showSignatureError, setShowSignatureError] = useState(false);

	const {
		formData,
		workOrder,
		currentUser,
		history,
		redirectForwardUrl,
		creating,
		updating,
		createErrors,
		updateErrors,
		onSuccess,
		onError,
		onReset,
		validateInvoiceNumber,
		parsedDataSnapshot,
		disableCosts,
	} = config;

	const { getIndicator } = useParsedFieldIndicator({
		parsedDataSnapshot,
		formInstance: form,
	});

	// Calculate if totals mismatch: parsed PDF total vs form's calculated total
	const totalsMismatch = useMemo(() => {
		if (!parsedDataSnapshot || !parsedDataSnapshot.invoiceTotalAfterTax) {
			return false;
		}

		const parsedTotal = parseFloat(parsedDataSnapshot.invoiceTotalAfterTax) || 0;
		const calculatedTotal = totalAfterTax;

		const difference = Math.abs(parsedTotal - calculatedTotal);
		return difference > 0.01; // Allow for small floating point differences
	}, [parsedDataSnapshot, totalAfterTax]);

	useEffect(() => {
		if (!formData) return;
		// Only initialize once per mount so we don't overwrite user input when formData/config refs change (e.g. Redux)
		if (formDataInitialized) return;

		const {
			labor,
			part,
			equipment,
			misc,
			travel,
			freight,
			incurred,
			formData: newFormData,
		} = initialiseFormDataWithLineItems(formData, timeIn);

		const initialValues: any = {
			id: formData.id,
			scope: nullSafeGet('scope', formData),
			attachments: formData.attachments || [],
		};

		if (config.fields.invoiceNumber) {
			initialValues.invoiceNumber = nullSafeGet('invoiceNumber', formData);
		}
		if (config.fields.externalWorkOrderId) {
			initialValues.externalWorkOrderId = nullSafeGet('externalWorkOrderId', formData);
		}
		if (config.fields.invoiceDate) {
			initialValues.dateOfInvoice = formData.dateOfInvoice ? moment(formData.dateOfInvoice) : null;
		}
		if (config.fields.paymentTerms) {
			initialValues.paymentTerms = nullSafeGet('paymentTerms', formData);
		}
		if (config.fields.dueDate) {
			initialValues.dueDate = formData.dueDate ? moment(formData.dueDate) : null;
		}

		form.setFieldsValue(initialValues);

		config.lineItemSections.forEach((section) => {
			if (section.show) {
				const lineItemsFieldName = `${section.type}LineItems`;
				const totalFieldName = `${section.type}Total`;
				const totalFieldNameBeforeTax = `${section.type}TotalBeforeTax`;

				let lineItems = [];
				let total = 0;

				if (section.type === 'parts') {
					const materialItems = formData.materialLineItems || [];
					lineItems = materialItems.filter((item) => item.partId);
					total = lineItems.reduce((sum, item) => sum + Number(item.amount), 0);
				} else if (section.type === 'equipment') {
					const materialItems = formData.materialLineItems || [];
					lineItems = materialItems.filter((item) => item.equipmentId);
					total = lineItems.reduce((sum, item) => sum + Number(item.amount), 0);
				} else {
					lineItems = formData[lineItemsFieldName] || [];
					total = formData[totalFieldNameBeforeTax] || 0;
				}

				const processedLineItems = lineItems.map((item) => ({
					...item,
					amount: Number(item.amount) || 0,
					hourlyRate: Number(item.hourlyRate) || 0,
					seconds: Number(item.seconds) || 0,
					numberOfTech: Number(item.numberOfTech) || 0,
					quantity: Number(item.quantity) || 0,
					unitPrice: Number(item.unitPrice) || 0,
				}));

				form.setFieldsValue({
					[lineItemsFieldName]: processedLineItems,
					[totalFieldName]: Number(total) || 0,
				});
			}
		});

		// Initialize tax
		if (formData.taxKeys) {
			setTaxKeys(formData.taxKeys);
			form.setFieldsValue({ taxKeys: formData.taxKeys });
			const taxSum = (formData.taxKeys || []).reduce((sum, k) => {
				const amt = Number(formData[`tax-${k}-amount`]) || 0;
				return sum + amt;
			}, 0);
			form.setFieldsValue({ invoiceTax: taxSum });
		} else if (formData.taxLineItems && formData.taxLineItems.length > 0) {
			const taxLineItems = formData.taxLineItems;
			const keys = taxLineItems.map((_, idx) => idx);
			setTaxKeys(keys);
			const taxFieldValues: Record<string, any> = { taxKeys: keys };
			let taxSum = 0;
			taxLineItems.forEach((lineItem, idx) => {
				const amount = Number(lineItem.amount) || 0;
				taxSum += amount;
				taxFieldValues[`tax-${idx}-taxType`] = nullSafeGet('taxType', lineItem) || 'Sales Tax';
				taxFieldValues[`tax-${idx}-amount`] = amount;
			});
			taxFieldValues.invoiceTax = taxSum;
			form.setFieldsValue(taxFieldValues);
		} else {
			// No tax line items: single tax only. Set invoiceTax so payload sends only invoiceTax.
			const invoiceTax =
				formData.invoiceTax != null && formData.invoiceTax !== ''
					? Number(formData.invoiceTax)
					: undefined;
			if (invoiceTax !== undefined && !Number.isNaN(invoiceTax)) {
				form.setFieldsValue({ invoiceTax });
			}
		}

		setFormDataInitialized(true);
	}, [formData, form, config, timeIn, formDataInitialized]);

	// Fetch currency internally
	useEffect(() => {
		if (!config.currency.fetchInternally || !workOrder) return;

		let fetchedCurrency = null;
		if (config.currency.source === 'workOrder') {
			fetchedCurrency = getCurrency({ workOrder });
		} else if (config.currency.source === 'supplierFacility') {
			fetchedCurrency = getCurrency({ supplier: nullSafeGet('supplierFacility', workOrder) });
		} else if (config.currency.source === 'quote') {
			// For quotes, currency comes from workOrder
			fetchedCurrency = getCurrency({ workOrder });
		}

		if (fetchedCurrency) {
			setCurrency(fetchedCurrency);

			// 1. If currency is CAD, fetch tax line items. 2. If API returns items, show line inputs.
			// 3. If parsed total exists (no line items from parser), put it in first line and rest 0.
			// Skip only when formData already has taxKeys/taxLineItems (e.g. loading existing invoice).
			const formData = config.formData || {};
			const alreadyHasTaxLineStructure =
				(formData.taxKeys && formData.taxKeys.length > 0) ||
				(formData.taxLineItems && formData.taxLineItems.length > 0);

			if (
				fetchedCurrency.id === 'CAD' &&
				config.tax.fetchTaxLineItems &&
				!alreadyHasTaxLineStructure
			) {
				config.tax
					.fetchTaxLineItems(fetchedCurrency.id)
					.then((data) => {
						if (data && data.length > 0) {
							const keys = data.map((_: any, idx: number) => idx);
							setTaxKeys(keys);
							const taxFieldValues: Record<string, any> = { taxKeys: keys };
							const parsedTotal =
								formData.invoiceTax != null && formData.invoiceTax !== ''
									? Number(formData.invoiceTax)
									: null;

							data.forEach((item: any, idx: number) => {
								// API may return array of strings (e.g. ["GST", "HST", "PST"]) or objects
								const taxType =
									typeof item === 'string'
										? item
										: item.taxType || item.tax_type || `Tax ${idx + 1}`;
								taxFieldValues[`tax-${idx}-taxType`] = taxType;
								if (parsedTotal != null && !Number.isNaN(parsedTotal)) {
									// Put parsed total in first line item, rest 0
									taxFieldValues[`tax-${idx}-amount`] = idx === 0 ? parsedTotal : 0;
								} else {
									taxFieldValues[`tax-${idx}-amount`] =
										item.amount != null ? Number(item.amount) : 0;
								}
							});
							if (parsedTotal != null && !Number.isNaN(parsedTotal)) {
								taxFieldValues.invoiceTax = parsedTotal;
							} else {
								taxFieldValues.invoiceTax = keys.reduce(
									(sum, idx) => sum + (taxFieldValues[`tax-${idx}-amount`] || 0),
									0
								);
							}
							form.setFieldsValue(taxFieldValues);
						}
					})
					.catch((err) => {
						console.error('Error fetching tax line items:', err);
					});
			}
		}
	}, [config.currency, config.formData, workOrder, form]);

	// Calculate totals
	const calculateTotalCost = useCallback(() => {
		let total = 0;
		config.lineItemSections.forEach((section) => {
			if (section.show) {
				const totalFieldName = `${section.type}Total`;
				const sectionTotal = Number(form.getFieldValue(totalFieldName)) || 0;
				total += sectionTotal;
			}
		});
		return Math.round(total * 100) / 100;
	}, [config.lineItemSections, form]);

	const calculateTaxAmount = useCallback(() => {
		const taxKeys = form.getFieldValue('taxKeys') || [];
		if (taxKeys.length === 0) {
			// No tax keys - read directly from invoiceTax field
			const invoiceTax = parseFloat(form.getFieldValue('invoiceTax')) || 0;
			return Math.round(invoiceTax * 100) / 100;
		}
		const taxTotal = taxKeys.reduce((sum, key) => {
			const amount = parseFloat(form.getFieldValue(`tax-${key}-amount`)) || 0;
			return sum + amount;
		}, 0);
		return Math.round(taxTotal * 100) / 100;
	}, [form]);

	const updateTotals = useCallback(() => {
		const newTotalBeforeTax = calculateTotalCost();
		const newTaxAmount = calculateTaxAmount();
		const newTotalAfterTax = newTotalBeforeTax + newTaxAmount;

		setTotalBeforeTax(newTotalBeforeTax);
		setTotalAfterTax(newTotalAfterTax);

		form.setFieldsValue({
			invoiceTotalBeforeTax: newTotalBeforeTax,
			invoiceTax: newTaxAmount,
			invoiceTotalAfterTax: newTotalAfterTax,
		});
	}, [calculateTotalCost, calculateTaxAmount, form]);

	// Handle form value changes
	const handleFormValuesChange = useCallback(() => {
		updateTotals();
	}, [updateTotals]);

	// Recalculate totals after form is initialized to ensure state matches calculated values from line items
	useEffect(() => {
		if (formDataInitialized) {
			updateTotals();
		}
	}, [formDataInitialized, updateTotals]);

	// Get scope from editor
	const getScope = useCallback(() => {
		return editorRef.current && (editorRef.current as any).getContent();
	}, []);

	// Handle invoice number validation
	const handleInvoiceNumberValidation = useCallback(
		async (rule, value, callback) => {
			if (!value || !validateInvoiceNumber) {
				callback();
				return;
			}

			try {
				const supplierFacilityId = nullSafeGet('supplierFacilityId', workOrder);
				const data = await validateInvoiceNumber(value, supplierFacilityId);
				if (data && Array.isArray(data)) {
					const isUpdate = formData.hasOwnProperty('id') && formData.id !== undefined;
					// Find first duplicate (don't call callback in loop)
					const duplicateInvoice = data.find((invoice) => {
						return (
							(isUpdate && nullSafeGet('id', invoice) !== nullSafeGet('id', formData)) || !isUpdate
						);
					});

					if (duplicateInvoice) {
						// Call callback only once with the first duplicate found
						callback(
							`Duplicate invoice number with Invoice ${nullSafeGet('id', duplicateInvoice)}`
						);
					} else {
						callback();
					}
				} else {
					callback();
				}
			} catch (error) {
				callback();
			}
		},
		[validateInvoiceNumber, workOrder, formData]
	);

	// Handle form submission
	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();
			try {
				const values = await form.validateFields();

				// Check if totals mismatch and signature is required
				if (totalsMismatch && !signatureName) {
					// Show signature modal instead of submitting
					setPendingSubmit(true);
					setShowSignatureModal(true);
					setShowSignatureError(false);
					return;
				}

				const scope = getScope();
				// If signature was provided, include it in the submission
				const submissionData = signatureName ? { ...values, signatureName } : values;

				await config.onSubmit(submissionData, form, scope);
			} catch (error) {
				if (onError) {
					onError(error);
				}
			}
		},
		[form, config, onError, getScope, signatureName, totalsMismatch]
	);

	// Handle signature and create invoice
	const handleSignAndCreate = useCallback(async () => {
		if (!signatureName || signatureName.trim() === '') {
			setShowSignatureError(true);
			message.error('Please enter your name to proceed');
			return;
		}

		try {
			setShowSignatureModal(false);
			const values = await form.validateFields();
			const scope = getScope();
			const submissionData = { ...values, signatureName };
			await config.onSubmit(submissionData, form, scope);
			setPendingSubmit(false);
		} catch (error) {
			if (onError) {
				onError(error);
			}
			setPendingSubmit(false);
		}
	}, [form, config, onError, getScope, signatureName]);

	// Handle payment term selection
	const handlePaymentTermSelect = useCallback(
		(val) => {
			const dueDate = getInvoiceDueDateBasedOnPaymentTerms(form.getFieldValue, val);
			form.setFieldsValue({
				paymentTerms: val,
				...(dueDate && { dueDate }),
			});
		},
		[form]
	);

	// Handle file attachment change
	const handleFileAttachmentChange = useCallback(
		(newAttachments) => {
			form.setFieldsValue({ attachments: newAttachments });
		},
		[form]
	);

	// Handle form reset
	const handleReset = useCallback(() => {
		// Reset form fields to initial values
		form.resetFields();

		// Reset state variables
		setTotalBeforeTax(0);
		setTotalAfterTax(0);
		setTaxKeys([]);
		setSignatureName('');
		setShowSignatureModal(false);
		setPendingSubmit(false);

		// Clear rich text editor
		if (editorRef.current) {
			(editorRef.current as any).setContent('');
		}

		// Call onReset callback if provided
		if (onReset) {
			onReset();
		}
	}, [form, onReset]);

	if (!formDataInitialized || !currency) {
		return <PageLoadingPlaceholder />;
	}

	const submitting = creating || updating;
	const errorMessagesText = (createErrors || []).concat(updateErrors || []).join('; ');
	const isUpdate = formData.hasOwnProperty('id') && formData.id !== undefined;
	const submitText = isUpdate
		? config.formType === 'proposal'
			? 'Update proposal'
			: 'Update invoice'
		: config.formType === 'proposal'
		? 'Create proposal'
		: 'Create invoice';

	return (
		<Form form={form} layout="vertical" onValuesChange={handleFormValuesChange}>
			{/* Scope */}
			{config.fields.scope && (
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item label="Scope" name="scope">
							<OWRichTextEditor
								initialEditorState={nullSafeGet('scope', formData)}
								editorRef={editorRef}
							/>
						</Form.Item>
					</Col>
				</Row>
			)}

			{/* File Attachments (for proposals) */}
			{config.fields.fileAttachments && (
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item label="File Attachments" name="attachments">
							<FileUploader
								defaultFileList={formData.attachments || []}
								roleType={currentUser && currentUser.userType}
								handleUploadSuccess={handleFileAttachmentChange}
								showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
								uploaderType="dragger"
							/>
						</Form.Item>
					</Col>
				</Row>
			)}
			<Row gutter={16}>
				{config.fields.invoiceNumber && (
					<Col span={12}>
						<Form.Item
							label="Invoice #"
							name="invoiceNumber"
							validateTrigger="onBlur"
							rules={[
								{ required: true, message: 'This field is required.' },
								...(validateInvoiceNumber
									? [
											{
												validator: handleInvoiceNumberValidation,
											},
									  ]
									: []),
							]}
						>
							<Input />
						</Form.Item>
					</Col>
				)}

				{config.fields.externalWorkOrderId && (
					<Col span={12}>
						<Form.Item
							label="Work Order #"
							name="externalWorkOrderId"
							validateTrigger="onBlur"
							rules={[{ required: true, message: 'This field is required.' }]}
						>
							<Input />
						</Form.Item>
					</Col>
				)}

				{config.fields.invoiceDate && (
					<Col span={12}>
						<Form.Item
							label="Invoice Date"
							name="dateOfInvoice"
							validateTrigger="onBlur"
							rules={[{ required: true, message: 'This field is required.' }]}
						>
							<DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
						</Form.Item>
					</Col>
				)}

				{config.fields.paymentTerms && (
					<Col span={12}>
						<Form.Item
							label="Payment Terms"
							name="paymentTerms"
							validateTrigger="onBlur"
							rules={[{ required: true, message: 'This field is required.' }]}
							style={{ marginBottom: 0 }}
						>
							<Input placeholder="Net 30" />
						</Form.Item>
						<div style={{ marginTop: 8, marginBottom: 16 }}>
							<InvoicePaymentTermComponent onSelect={handlePaymentTermSelect} />
						</div>
					</Col>
				)}

				{config.fields.dueDate && (
					<Col span={12}>
						<Form.Item label="Due Date" name="dueDate">
							<DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
						</Form.Item>
					</Col>
				)}

				{config.fields.status && (
					<Col span={12}>
						<Form.Item label="Has this invoice been paid?" name="status" initialValue="pending">
							<Radio.Group>
								<Radio.Button value="pending">No</Radio.Button>
								<Radio.Button value="paid">Yes</Radio.Button>
							</Radio.Group>
						</Form.Item>
					</Col>
				)}
			</Row>
			{config.lineItemSections
				.filter((section) => section.show)
				.map((section) => (
					<CostSection
						key={section.type}
						label={section.config.label}
						form={form}
						fieldKey={section.type}
						currency={currency}
						config={section.config}
						onTotalChange={updateTotals}
						parsedDataSnapshot={parsedDataSnapshot}
						disabled={disableCosts}
					/>
				))}

			<Row gutter={16}>
				<Col span={24}>
					<div className="my-6 flex flex-row items-center gap-x-4">
						<div className="h5medium whitespace-nowrap">Total Before Tax</div>
						<DotSeparator />
						<div className="flex items-center gap-2 whitespace-nowrap pl-4">
							{currency.format(totalBeforeTax.toFixed(2))}
						</div>
					</div>
				</Col>

				<Col span={24}>
					<TaxFormFieldsNew
						form={form}
						formData={formData}
						currency={currency}
						onTaxChange={updateTotals}
						disabled={disableCosts}
						taxKeysFromParent={taxKeys}
						getIndicator={getIndicator}
					/>
				</Col>

				<Col span={24}>
					<div className="my-6 flex flex-row items-center gap-x-4">
						<div className="h5medium whitespace-nowrap">Total After Tax</div>
						<DotSeparator />
						<div className="h5bold flex items-center gap-2 whitespace-nowrap pl-4">
							{getIndicator('invoiceTotalAfterTax')}
							{currency.format(totalAfterTax.toFixed(2))}
						</div>
					</div>
				</Col>
				{totalsMismatch && !signatureName && (
					<Col span={24} className="mb-4">
						<div
							className="flex flex-row items-start justify-start gap-x-3 p-3"
							style={{ backgroundColor: 'rgba(255, 77, 79, 0.10)' }}
						>
							<ExclamationCircleFilled
								style={{
									color: '#FF4D4F',
									lineHeight: '28px',
								}}
							/>
							<div className="flex flex-col gap-y-3">
								<div className="bodyregular text-character-primary">
									The invoice total from the PDF doesn't match the total after tax here. Review line
									items to find any missing or incorrect entries.{' '}
								</div>
								<div className="bodyregular text-character-primary">
									If everything looks correct, sign to proceed.
								</div>
								<div className="flex flex-row gap-x-3">
									<Button type="primary" onClick={() => setShowSignatureModal(true)}>
										Sign to proceed
									</Button>
									<Popconfirm
										title="Are you sure you? Any unsaved changes will be lost."
										onConfirm={handleReset}
										okText="Yes"
										cancelText="No"
									>
										<Button>Unable to fix, create from scratch</Button>
									</Popconfirm>
								</div>
							</div>
						</div>
					</Col>
				)}
				{disableCosts ? (
					<Col span={24} className="mb-4">
						<div
							className="flex flex-row items-start justify-start gap-x-3 p-3"
							style={{ backgroundColor: 'rgba(255, 77, 79, 0.10)' }}
						>
							<ExclamationCircleFilled
								style={{
									color: '#FAAD14',
									lineHeight: '28px',
								}}
							/>
							<div className="flex flex-col gap-y-3">
								<div className="bodyregular text-character-primary">
									Discounts are applied to every line item. If changes need to be made, clear all
									data and create invoice manually.
								</div>

								<div className="flex flex-row gap-x-3">
									<Popconfirm
										title="Are you sure? All changes will be lost."
										onConfirm={handleReset}
										okText="Yes"
										cancelText="No"
									>
										<Button>Clear data & create from scratch</Button>
									</Popconfirm>
								</div>
							</div>
						</div>
					</Col>
				) : null}

				{errorMessagesText && (
					<Col span={24}>
						<Form.Item label="">
							<Alert message={errorMessagesText} type="error" />
						</Form.Item>
					</Col>
				)}

				<Col span={24}>
					<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
						<Popconfirm
							title="Are you sure you want to cancel? Unsaved changes will be lost."
							onConfirm={handleCancel}
							okText="Yes"
							cancelText="No"
							disabled={submitting}
						>
							<Button loading={submitting} size="large">
								Cancel
							</Button>
						</Popconfirm>
						<Button
							disabled={totalsMismatch && !signatureName}
							type="primary"
							htmlType="submit"
							loading={submitting}
							onClick={handleSubmit}
							size="large"
						>
							{submitText}
						</Button>
					</div>
				</Col>
			</Row>

			<Modal
				title={
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<ExclamationCircleOutlined style={{ color: '#ef4444', fontSize: 24 }} />
						<span className="h5medium text-character-primary">
							Totals don't match. Sign below to create invoice
						</span>
					</div>
				}
				visible={showSignatureModal}
				onCancel={() => {
					setShowSignatureModal(false);
					setPendingSubmit(false);
					setShowSignatureError(false);
					setSignatureName(''); // Reset signature on cancel
				}}
				footer={null}
				width={600}
			>
				<div className="bodyregular mb-4 flex flex-col text-character-primary">
					<div>The invoice total from the PDF doesn't match the 'Total with Tax'.</div>
					<div>
						If all line items have been reviewed and are correct, sign below to create the invoice.
					</div>
				</div>
				<Form.Item
					label=""
					validateStatus={showSignatureError && signatureName.trim() === '' ? 'error' : ''}
					help={
						showSignatureError && signatureName.trim() === ''
							? 'Please enter your name to proceed'
							: ''
					}
				>
					<Input
						placeholder="John Doe"
						value={signatureName}
						onChange={(e) => setSignatureName(e.target.value)}
						onPressEnter={handleSignAndCreate}
					/>
					<div style={{ color: '#6b7280', fontSize: 14, marginTop: 8 }}>
						By entering your name, you consent to its use as your signature
					</div>
				</Form.Item>
				<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
					<Button
						onClick={() => {
							setShowSignatureModal(false);
							setPendingSubmit(false);
							setShowSignatureError(false);
							setSignatureName(''); // Reset signature on cancel
						}}
					>
						Cancel
					</Button>
					<Button
						type="primary"
						onClick={handleSignAndCreate}
						disabled={!signatureName || signatureName.trim() === ''}
						loading={submitting}
					>
						Sign & Create Invoice
					</Button>
				</div>
			</Modal>
		</Form>
	);
};

export default InvoiceFormBase;
